import { useMemo, useState } from "react";
import {
  useGetPortfolioBreakdownQuery,
  type BuyDistanceBucket,
  type PortfolioOpenOrder,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { fmtUsd, fmtPrice, fmtQty } from "@/lib/format";

// Distance-bucket presentation. Order matters: imminent (closest to fill) → parked.
const BUCKET_ORDER: BuyDistanceBucket[] = ["imminent", "near", "deep", "parked"];

const bucketLabel: Record<BuyDistanceBucket, string> = {
  imminent: "Imminent",
  near: "Near",
  deep: "Deep",
  parked: "Parked",
};

const bucketSub: Record<BuyDistanceBucket, string> = {
  imminent: "≤ 1%",
  near: "1–3%",
  deep: "3–6%",
  parked: "> 6%",
};

const bucketTone: Record<BuyDistanceBucket, BadgeTone> = {
  imminent: "good",
  near: "info",
  deep: "warn",
  parked: "muted",
};

// Solid bar colors for the stacked liquidity bar (Tailwind palette).
const bucketBarColor: Record<BuyDistanceBucket, string> = {
  imminent: "bg-emerald-500",
  near: "bg-sky-500",
  deep: "bg-amber-500",
  parked: "bg-rose-500/70",
};

type SideFilter = "all" | "buy" | "sell";

export default function Holdings() {
  const q = useGetPortfolioBreakdownQuery({ min_value_usd: 5 }, { pollingInterval: 30000 });
  const [sideFilter, setSideFilter] = useState<SideFilter>("all");

  const summary = q.data?.summary;
  const assets = q.data?.assets ?? [];
  const orders = q.data?.orders ?? [];

  const buckets = summary?.buy_distance_buckets;
  const bucketTotal = useMemo(
    () => (buckets ? BUCKET_ORDER.reduce((s, b) => s + (buckets[b] ?? 0), 0) : 0),
    [buckets],
  );

  // Filter by side, then sort by distance ascending within each side
  // (closest-to-fill first — the key thing the user wants to see).
  const visibleOrders = useMemo(() => {
    const rows = orders.filter((o) => sideFilter === "all" || o.side === sideFilter);
    return [...rows].sort((a, b) => {
      if (a.side !== b.side) return a.side === "buy" ? -1 : 1;
      return a.distance_pct - b.distance_pct;
    });
  }, [orders, sideFilter]);

  return (
    <div className="space-y-4">
      {/* ====== Summary cards ====== */}
      <Card>
        <CardHeader>
          <CardTitle>
            Holdings overview
            <span className="text-xs font-normal text-muted ml-2">
              live portfolio snapshot · auto-refreshes every 30s
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !summary ? (
            <p className="text-sm text-muted">No portfolio data available.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Tile label="Total portfolio" value={fmtUsd(summary.total_portfolio_usd)} />
              <Tile label="Holdings value" value={fmtUsd(summary.holdings_usd)} />
              <Tile label="USDT free" value={fmtUsd(summary.usdt_free)} tone="good" />
              <Tile label="USDT locked" value={fmtUsd(summary.usdt_locked)} tone="muted" />
              <Tile label="Open buy total" value={fmtUsd(summary.open_buy_usd)} />
              <Tile label="Open sell total" value={fmtUsd(summary.open_sell_usd)} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ====== Buy liquidity by distance-to-fill ====== */}
      <Card>
        <CardHeader>
          <CardTitle>
            Buy liquidity by distance-to-fill
            <span className="text-xs font-normal text-muted ml-2">
              how far the market must move for each open buy to fill
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {q.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : !buckets ? (
            <p className="text-sm text-muted">No open buy liquidity.</p>
          ) : (
            <>
              {/* Stacked bar */}
              <div className="flex h-3 w-full overflow-hidden rounded bg-bg">
                {bucketTotal > 0 &&
                  BUCKET_ORDER.map((b) => {
                    const v = buckets[b] ?? 0;
                    if (v <= 0) return null;
                    return (
                      <div
                        key={b}
                        className={bucketBarColor[b]}
                        style={{ width: `${(v / bucketTotal) * 100}%` }}
                        title={`${bucketLabel[b]} (${bucketSub[b]}): ${fmtUsd(v)}`}
                      />
                    );
                  })}
              </div>
              {/* Labeled chips */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {BUCKET_ORDER.map((b) => {
                  const v = buckets[b] ?? 0;
                  const pct = bucketTotal > 0 ? (v / bucketTotal) * 100 : 0;
                  return (
                    <div key={b} className="rounded-lg border border-border bg-bg2 p-3">
                      <div className="flex items-center justify-between">
                        <Badge tone={bucketTone[b]}>{bucketLabel[b]}</Badge>
                        <span className="text-[10px] text-muted">{bucketSub[b]}</span>
                      </div>
                      <div className="mt-1 text-lg font-semibold tabular">{fmtUsd(v)}</div>
                      <div className="text-xs text-muted">{pct.toFixed(0)}% of buy liquidity</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ====== Per-asset table ====== */}
      <Card>
        <CardHeader>
          <CardTitle>
            Assets
            <span className="text-xs font-normal text-muted ml-2">
              {assets.length} holdings · sorted by value
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : assets.length === 0 ? (
            <p className="text-sm text-muted">No holdings above the minimum value.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Asset</TH>
                  <TH className="text-right">Qty</TH>
                  <TH className="text-right">Price</TH>
                  <TH className="text-right">Value</TH>
                  <TH className="text-right">% of portfolio</TH>
                  <TH className="text-right">Locked qty</TH>
                </TR>
              </THead>
              <TBody>
                {assets.map((a) => (
                  <TR key={a.asset}>
                    <TD className="font-mono">{a.asset}</TD>
                    <TD className="text-right tabular">{fmtQty(a.qty)}</TD>
                    <TD className="text-right tabular">{fmtPrice(a.price)}</TD>
                    <TD className="text-right tabular">{fmtUsd(a.value_usd)}</TD>
                    <TD className="text-right tabular">{a.pct.toFixed(2)}%</TD>
                    <TD className="text-right tabular text-muted">
                      {a.locked_qty > 0 ? fmtQty(a.locked_qty) : "—"}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ====== Open orders w/ distance-to-fill ====== */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>
              Open orders
              <span className="text-xs font-normal text-muted ml-2">
                {visibleOrders.length} shown · sorted by distance-to-fill
              </span>
            </span>
            <div className="flex items-center gap-1">
              {(["all", "buy", "sell"] as SideFilter[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSideFilter(s)}
                  className={
                    "px-2 py-0.5 rounded-md text-xs capitalize border " +
                    (sideFilter === s
                      ? "bg-bg text-fg border-border"
                      : "text-muted border-transparent hover:text-fg hover:bg-bg")
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : visibleOrders.length === 0 ? (
            <p className="text-sm text-muted">No open orders.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Symbol</TH>
                  <TH>Side</TH>
                  <TH className="text-right">Price</TH>
                  <TH className="text-right">Market</TH>
                  <TH className="text-right">Distance</TH>
                  <TH className="text-right">Notional</TH>
                  <TH>Bucket</TH>
                </TR>
              </THead>
              <TBody>
                {visibleOrders.map((o, i) => (
                  <OrderRow key={`${o.symbol}-${o.side}-${o.price}-${i}`} o={o} />
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function OrderRow({ o }: { o: PortfolioOpenOrder }) {
  return (
    <TR>
      <TD className="font-mono">{o.symbol}</TD>
      <TD>
        <span className={o.side === "buy" ? "text-good font-medium" : "text-bad font-medium"}>
          {o.side.toUpperCase()}
        </span>
      </TD>
      <TD className="text-right tabular">{fmtPrice(o.price)}</TD>
      <TD className="text-right tabular text-muted">{fmtPrice(o.market)}</TD>
      <TD className="text-right tabular">{o.distance_pct.toFixed(2)}%</TD>
      <TD className="text-right tabular">{fmtUsd(o.notional_usd)}</TD>
      <TD>
        <Badge tone={bucketTone[o.bucket]}>{bucketLabel[o.bucket]}</Badge>
      </TD>
    </TR>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  const cls =
    tone === "good"
      ? "border-emerald-500/40"
      : tone === "bad"
      ? "border-rose-500/40"
      : tone === "warn"
      ? "border-amber-500/40"
      : "border-border";
  return (
    <div className={`rounded-lg border ${cls} bg-bg2 p-3`}>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold tabular">{value}</div>
    </div>
  );
}
